/**
 * IIFE Entry Point
 *
 * This file is the entry point for the IIFE build, which bundles React
 * and creates a custom element (<apex-widget>) for easy embedding.
 *
 * Usage:
 * ```html
 * <script src="https://cdn.example.com/apex-widget.js"></script>
 * <apex-widget
 *   api-url="https://api.example.com"
 *   api-key="apex_wk_..."
 *   theme="light"
 *   data-cta="210 90% 50%"
 * ></apex-widget>
 * ```
 */

import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { Widget, type WidgetProps } from "./Widget";
import { applyThemeOverrides, type ThemeOverrides } from "./hooks/useTheme";
import type { ThemeMode } from "./hooks/useTheme";

// Import compiled styles as a string for Shadow DOM injection
import widgetStyles from "./styles/base.css?inline";

/** Google Fonts URL for DM Sans */
const FONT_URL =
  "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap";

/** Sentinel to inject font link only once per document */
const FONT_LINK_ID = "apex-widget-font";

/**
 * Observed attributes for the custom element
 */
const OBSERVED_ATTRIBUTES = [
  "api-url",
  "api-key",
  "initial-item",
  "initial-items",
  "theme",
  "height",
  "width",
  // px offset for the widget's sticky elements (category bar, compact bar).
  // Set this to the height of the host page's fixed/sticky header when the
  // widget is embedded in auto (page-flow) mode, so the sticky bars pin below
  // it instead of behind it. Default 0.
  "sticky-top",
] as const;

/**
 * ApexWidgetElement - Custom HTML Element
 *
 * A web component wrapper for the Apex Widget React component.
 * Uses Shadow DOM for style isolation.
 */
/**
 * Grace window for the "Missing required attributes" diagnostic (B3).
 * Host pages frequently mount `<apex-widget>` and then set api-url/api-key
 * from an inline <script> a tick later. Logging immediately on
 * connectedCallback produces a spurious console.error pair that worries
 * embedders and clutters real diagnostics. Defer the warning until we've
 * given attributeChangedCallback a fair chance to deliver the missing
 * values; if they still aren't present after the window, log for real.
 */
const ATTRIBUTE_VALIDATION_GRACE_MS = 100;

class ApexWidgetElement extends HTMLElement {
  private root: Root | null = null;
  private mountPoint: HTMLDivElement | null = null;
  private styleElement: HTMLStyleElement | null = null;
  private pendingValidationTimer: ReturnType<typeof setTimeout> | null = null;

  static get observedAttributes(): readonly string[] {
    return OBSERVED_ATTRIBUTES;
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    // Set initial data-theme on host for :host([data-theme]) CSS selector
    const theme = this.getAttribute("theme") || "light";
    this.dataset.theme = theme;

    // Custom elements are inline by default — ensure block layout and apply size
    this.style.display = "block";
    this.applyHostSize();
    this.applyStickyOffset();

    this.injectFont();
    this.setupShadowDOM();
    this.render();
  }

  disconnectedCallback(): void {
    if (this.pendingValidationTimer) {
      clearTimeout(this.pendingValidationTimer);
      this.pendingValidationTimer = null;
    }
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue && this.mountPoint) {
      // Mirror theme to host element for :host([data-theme]) CSS selector
      if (name === "theme" && newValue) {
        this.dataset.theme = newValue;
      }
      // Keep host element size in sync with height/width attributes
      if (name === "height" || name === "width") {
        this.applyHostSize();
      }
      // Sticky offset is a pure CSS-variable change — no re-render needed,
      // but it's cheap and the render below is harmless.
      if (name === "sticky-top") {
        this.applyStickyOffset();
      }
      this.render();
    }
  }

  /**
   * Mirror height/width attributes to the host element's CSS so
   * percentage-based sizing cascades correctly through the Shadow DOM.
   *
   * Height defaults to "auto" — the widget grows to its content, only the
   * page scrolls. Customers can opt into a fixed height ("100vh", "800",
   * "75%", etc.) by setting the attribute; the host then locks to that size
   * and the inner views scroll internally if their content overflows. The
   * `data-embed-mode` attribute lets shadow-DOM CSS branch between the two.
   */
  private applyHostSize(): void {
    const heightAttr = this.getAttribute("height");
    const width = this.getAttribute("width") || "100%";

    const isAutoHeight = !heightAttr || heightAttr === "auto";
    if (isAutoHeight) {
      this.style.height = "auto";
      this.dataset.embedMode = "auto";
    } else {
      this.style.height = isNaN(Number(heightAttr))
        ? heightAttr
        : `${heightAttr}px`;
      this.dataset.embedMode = "fixed";
    }
    this.style.width = isNaN(Number(width)) ? width : `${width}px`;
  }

  /**
   * Mirror the `sticky-top` attribute to the `--apex-sticky-offset` CSS custom
   * property on the host. Custom properties inherit across the shadow boundary,
   * so the widget's sticky elements (category bar, compact bar) and its
   * scroll-into-view margins read it. Used in auto (page-flow) embeds to push
   * the sticky bars below the host page's fixed header. Bare numbers are treated
   * as px. Unset/empty removes the override (defaults to 0 via base.css).
   */
  private applyStickyOffset(): void {
    const value = this.getAttribute("sticky-top");
    if (value && value.trim() !== "") {
      const px = isNaN(Number(value)) ? value : `${value}px`;
      this.style.setProperty("--apex-sticky-offset", px);
    } else {
      this.style.removeProperty("--apex-sticky-offset");
    }
  }

  /**
   * Inject Google Fonts <link> into document.head (once per page)
   */
  private injectFont(): void {
    if (document.getElementById(FONT_LINK_ID)) return;

    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = FONT_URL;
    document.head.appendChild(link);
  }

  /**
   * Set up Shadow DOM with styles and mount point
   */
  private setupShadowDOM(): void {
    if (!this.shadowRoot) return;

    // Create style element for Tailwind CSS
    this.styleElement = document.createElement("style");
    this.styleElement.textContent = this.getStyles();
    this.shadowRoot.appendChild(this.styleElement);

    // Create mount point — fills the host element
    this.mountPoint = document.createElement("div");
    this.mountPoint.className = "apex-widget-mount";
    this.mountPoint.style.height = "100%";
    this.mountPoint.style.width = "100%";
    this.shadowRoot.appendChild(this.mountPoint);

    // Apply theme overrides if provided
    const themeOverrides = this.getThemeOverrides();
    if (Object.keys(themeOverrides).length > 0) {
      applyThemeOverrides(this.mountPoint, themeOverrides);
    }
  }

  /**
   * Get compiled Tailwind CSS to inject into Shadow DOM
   */
  private getStyles(): string {
    return widgetStyles;
  }

  /**
   * Get theme overrides from data attributes
   */
  private getThemeOverrides(): ThemeOverrides {
    const overrides: ThemeOverrides = {};

    const primary = this.dataset.primary;
    if (primary) overrides.primary = primary;

    const background = this.dataset.background;
    if (background) overrides.background = background;

    const foreground = this.dataset.foreground;
    if (foreground) overrides.foreground = foreground;

    const radius = this.dataset.radius;
    if (radius) overrides.radius = radius;

    const fontFamily = this.dataset.fontFamily;
    if (fontFamily) overrides.fontFamily = fontFamily;

    const cta = this.dataset.cta;
    if (cta) overrides.cta = cta;

    return overrides;
  }

  /**
   * Get widget props from attributes
   */
  private getProps(): WidgetProps {
    const apiUrl = this.getAttribute("api-url") || "";
    const apiKey = this.getAttribute("api-key") || "";
    const initialRootItem = this.getAttribute("initial-item") || undefined;
    const initialItemsAttr = this.getAttribute("initial-items");
    const initialItems = initialItemsAttr
      ? initialItemsAttr.split(",").map((s) => s.trim())
      : undefined;
    const theme = (this.getAttribute("theme") as ThemeMode) || "light";
    // height defaults to "auto" so the widget grows to content and only the
    // page scrolls. Customers can override with a fixed value (e.g. "100vh").
    const heightAttr = this.getAttribute("height");
    const height = heightAttr === null || heightAttr === "" ? "auto" : heightAttr;
    const width = this.getAttribute("width") || "100%";

    return {
      apiUrl,
      apiKey,
      initialRootItem,
      initialItems,
      theme,
      height: isNaN(Number(height)) ? height : Number(height),
      width: isNaN(Number(width)) ? width : Number(width),
    };
  }

  private renderLoadingPlaceholder(): void {
    if (!this.mountPoint) return;
    this.mountPoint.replaceChildren();
    const placeholder = document.createElement("div");
    placeholder.style.padding = "20px";
    placeholder.style.textAlign = "center";
    placeholder.style.color = "#999";
    placeholder.style.fontSize = "12px";
    placeholder.textContent = "Loading…";
    this.mountPoint.appendChild(placeholder);
  }

  private renderConfigErrorPlaceholder(): void {
    if (!this.mountPoint) return;
    this.mountPoint.replaceChildren();
    const wrapper = document.createElement("div");
    wrapper.style.padding = "20px";
    wrapper.style.textAlign = "center";
    wrapper.style.color = "#666";
    const title = document.createElement("p");
    title.textContent = "Widget configuration error";
    const detail = document.createElement("p");
    detail.style.fontSize = "12px";
    detail.textContent = "Missing api-url or api-key attribute";
    wrapper.appendChild(title);
    wrapper.appendChild(detail);
    this.mountPoint.appendChild(wrapper);
  }

  /**
   * Render the React component
   */
  private render(): void {
    if (!this.mountPoint) return;

    const props = this.getProps();

    // Validate required props. Defer the actual log + error placeholder so a
    // host page that sets attributes a tick after appending the element
    // doesn't trip the warning (B3); if attributes never arrive, the timer
    // fires and we surface the same diagnostic as before.
    if (!props.apiUrl || !props.apiKey) {
      this.renderLoadingPlaceholder();
      if (this.pendingValidationTimer) clearTimeout(this.pendingValidationTimer);
      this.pendingValidationTimer = setTimeout(() => {
        this.pendingValidationTimer = null;
        const latest = this.getProps();
        if (latest.apiUrl && latest.apiKey) {
          this.render();
          return;
        }
        console.error(
          "[ApexWidget] Missing required attributes: api-url and api-key are required"
        );
        this.renderConfigErrorPlaceholder();
      }, ATTRIBUTE_VALIDATION_GRACE_MS);
      return;
    }

    if (this.pendingValidationTimer) {
      clearTimeout(this.pendingValidationTimer);
      this.pendingValidationTimer = null;
    }

    // Create or update React root
    if (!this.root) {
      this.root = createRoot(this.mountPoint);
    }

    this.root.render(
      <React.StrictMode>
        <Widget {...props} />
      </React.StrictMode>
    );
  }

  /**
   * Public API: Update widget configuration
   */
  public updateConfig(config: Partial<WidgetProps>): void {
    Object.entries(config).forEach(([key, value]) => {
      if (value !== undefined) {
        const attrName = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        this.setAttribute(attrName, String(value));
      }
    });
  }
}

// Register custom element
if (typeof window !== "undefined" && !customElements.get("apex-widget")) {
  customElements.define("apex-widget", ApexWidgetElement);
}

// Export for potential programmatic usage
export { ApexWidgetElement };
export type { WidgetProps };
